import type { BranchKey } from '@festina-life/api'
import { errorMessageToastContent } from '@festina-life/components'
import { assertNotEmpty } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { toast } from '@festina-life/ui/feedback'
import { Select } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Popover, PopoverButton } from '@festina-life/ui/structure'
import { useMemo, useState } from 'react'

import { useMetaModelContext } from '../../../../context/metaModelContext'
import { collectAllNodesFromTree } from '../../../../uiModels/api/metaModel/helpers'
import { usePublishChangesetsForBranch } from '../../../../uiModels/branches/usePublishChangesetsForBranch'
import { ServerServices } from '../../../../uiModels/openApiServices'
import { ActionsMenuPanel } from '../../../actionsPanel/ActionsMenuPanel'

type Props = {
  branchKey: BranchKey
}

export const PublishConfigurationToServerButton = ({ branchKey }: Props) => {
  const translate = useTranslate()
  const {
    mutateAsync: publishChangesetsToServer,
    isPending: isPublishingChangesets,
  } = usePublishChangesetsForBranch()
  const { blockTopology } = useMetaModelContext()
  const allBlocks = useMemo(
    () => (blockTopology ? collectAllNodesFromTree(blockTopology) : []),
    [blockTopology],
  )
  const firstBlock = allBlocks.at(0)

  assertNotEmpty(firstBlock, translate('error.noBlocks'))
  const [block, setBlock] = useState(firstBlock)

  const isServerConfigured = !!ServerServices[branchKey.server].api.OpenAPI.BASE

  const handlePublishBranchChangesets = async (close: () => void) => {
    try {
      await publishChangesetsToServer({ branchKey, block })
      toast({
        title: translate('branchDetailsPanel.tabs.advanced.publish.success', {
          branchName: branchKey.name,
          server: branchKey.server,
        }),
        variant: 'success',
      })
      close()
    } catch (error) {
      toast(errorMessageToastContent({ error, translate }))
    }
  }

  return (
    <Popover>
      {({ close }) => (
        <>
          <PopoverButton as="div">
            <Button
              startIcon="CloudArrowUp"
              disabled={isPublishingChangesets || !isServerConfigured}
            >
              {translate('branchDetailsPanel.tabs.advanced.publishToServer', {
                server: branchKey.server,
              })}
            </Button>
          </PopoverButton>
          <ActionsMenuPanel
            anchor={{ to: 'right' }}
            className="max-w-lg! flex flex-col gap-2"
          >
            <Select
              options={allBlocks}
              wrapperProps={{
                label: translate(
                  'branchDetailsPanel.tabs.advanced.publishToServer.block.label',
                ),
              }}
              shouldShowClearButton={false}
              value={block}
              onChange={(nextValue) => {
                if (nextValue) {
                  setBlock(nextValue)
                }
              }}
            />
            <Button
              onClick={() => handlePublishBranchChangesets(close)}
              isLoading={isPublishingChangesets}
              className="self-end"
            >
              {translate(
                'branchDetailsPanel.tabs.advanced.publishToServer.confirmButton',
              )}
            </Button>
          </ActionsMenuPanel>
        </>
      )}
    </Popover>
  )
}
